/**
 * "Next case" for the surfacer (report review) viewer: the next study after the
 * current one, in the worklist's default order, that the worklist would still
 * list as outstanding.
 *
 * Outstanding mirrors the worklist's own row rules (Worklist.tsx studyIndex +
 * indexFilteredStudyIds): a reporter row (`comparison_counts`) with at least one
 * surfaced finding, not skipped, and not fully reviewed (every surfaced finding
 * carrying a non-pending verdict). Order mirrors the worklist's "default" sort
 * for reporter cohorts: `compareUrgency` (OP advancement desc, then cohort
 * percentile desc), stable over the backend's row order, and the backend order
 * untouched when no row carries an urgency score.
 *
 * With "Only unjudged" on, a study also needs at least one finding nobody in the
 * version family has judged (`unjudgedFindings`); fully judged studies are
 * skipped even when the reader has given none of those verdicts themselves.
 *
 * It does not replay the worklist's ad-hoc UI filters (search, finding chips,
 * batch, a non-default sort): those live in Worklist component state that the
 * case viewer can't see. The walk starts after the current study and wraps.
 */

import type { ReaderStudyItem } from "../hooks/useStudyQueries";
import type { AllDecisionsMap } from "../hooks/useSurfacerDecisions";
import type { SkippedMap } from "../hooks/useSkippedStudies";
import type { ReviewStatesByCohort } from "../hooks/useSurfacerReviewState";
import { compareUrgency } from "./findingPriority";

/**
 * Findings on this row that nobody has judged: the backend's pooled
 * `unjudged_findings`, minus any the reader has since given a verdict to in this
 * cohort (the review state is re-read only once a study's writes drain; the
 * reader's own decisions are refreshed on every write). Null while the row's
 * cohort review state hasn't loaded. A row the review state leaves out has no
 * surfacer candidates, so nothing to judge.
 */
export function unjudgedFindings(
  item: ReaderStudyItem,
  fallbackCohortId: string,
  reviewStates: ReviewStatesByCohort,
  decisions: AllDecisionsMap,
): string[] | null {
  const cohortId = item.cohort_id ?? fallbackCohortId;
  const state = reviewStates[cohortId];
  if (!state) return null;
  const own = decisions[cohortId]?.[item.study_uid];
  return (state.studies[item.study_uid]?.unjudged_findings ?? []).filter((finding) => {
    const verdict = own?.[finding];
    return verdict == null || verdict === "pending";
  });
}

/**
 * "N unjudged left": findings nobody has judged across the reporter rows of a
 * list, and how many cases hold them. Skipped cases are set aside, so they
 * don't count. Rows whose cohort review state hasn't loaded are left out.
 */
export function unjudgedTally(
  studies: readonly ReaderStudyItem[],
  fallbackCohortId: string,
  reviewStates: ReviewStatesByCohort,
  decisions: AllDecisionsMap,
  skipped: SkippedMap,
): { findings: number; cases: number } {
  let findings = 0;
  let cases = 0;
  for (const item of studies) {
    if (!item.comparison_counts || skipped[item.study_uid]) continue;
    const left = unjudgedFindings(item, fallbackCohortId, reviewStates, decisions)?.length ?? 0;
    if (left > 0) {
      findings += left;
      cases += 1;
    }
  }
  return { findings, cases };
}

function isOutstanding(
  item: ReaderStudyItem,
  fallbackCohortId: string,
  decisions: AllDecisionsMap,
  skipped: SkippedMap,
): boolean {
  if (!item.comparison_counts) return false;
  const findings = item.surfacer_findings ?? [];
  if (findings.length === 0) return false;
  if (skipped[item.study_uid]) return false;
  const studyDecisions = decisions[item.cohort_id ?? fallbackCohortId]?.[item.study_uid];
  return !findings.every((f) => {
    const verdict = studyDecisions?.[f.finding];
    return verdict != null && verdict !== "pending";
  });
}

/** Rows in the worklist's default reporter order. */
export function surfacerWorklistOrder(studies: readonly ReaderStudyItem[]): ReaderStudyItem[] {
  if (!studies.some((s) => s.urgency_score != null)) return [...studies];
  const urgency = (s: ReaderStudyItem) => ({
    urgencyScore: s.urgency_score ?? null,
    urgencyPercentile: s.urgency_percentile ?? null,
  });
  return [...studies].sort((a, b) => compareUrgency(urgency(a), urgency(b)));
}

/**
 * The next outstanding study after `currentUid`, wrapping, never the current one.
 * `fallbackCohortId` is the cohort of rows that don't carry their own
 * `cohort_id` (a single-cohort response). Null when nothing else is outstanding.
 *
 * `reviewStates` turns on "Only unjudged": a study whose findings have all been
 * judged by someone in the family is skipped. Pass null for the reader's own
 * verdicts only. A study whose cohort review state hasn't loaded is not skipped.
 */
export function nextUnreviewedSurfacerStudy(
  studies: readonly ReaderStudyItem[],
  currentUid: string,
  fallbackCohortId: string,
  decisions: AllDecisionsMap,
  skipped: SkippedMap,
  reviewStates: ReviewStatesByCohort | null = null,
): ReaderStudyItem | null {
  const ordered = surfacerWorklistOrder(studies);
  const start = ordered.findIndex((s) => s.study_uid === currentUid);
  for (let off = 1; off <= ordered.length; off++) {
    const s = ordered[(start + off) % ordered.length];
    if (s.study_uid === currentUid) continue;
    if (!isOutstanding(s, fallbackCohortId, decisions, skipped)) continue;
    if (reviewStates) {
      const left = unjudgedFindings(s, fallbackCohortId, reviewStates, decisions);
      if (left !== null && left.length === 0) continue;
    }
    return s;
  }
  return null;
}
